import type { IChatLocalMessage, IChatMessage } from "@/types/(my-space)/chat.interface";
import { compareChatMessages, upsertMessage } from "./chat-utils";

/**
 * The room shows a contiguous window of the conversation. Normally it ends at
 * the newest message ("attached": live messages append). After jumping to an
 * older message (search result, reply quote) it is "detached": a slice in the
 * middle, with a gap between its last message and the live end — live
 * messages must not be appended into that gap.
 *
 * While detached, our own optimistic messages (sending / failed) are kept
 * aside in a stash: they belong after the newest message, not after the
 * window's last one, and they come back when the window re-attaches.
 */

const sameMessage = (a: IChatMessage, b: IChatMessage) =>
  a.id === b.id || (b.clientMessageId != null && a.clientMessageId === b.clientMessageId);

/** A history page (older or newer) merged into the window, without duplicates. */
export const mergePage = (
  list: IChatLocalMessage[],
  page: IChatMessage[],
): IChatLocalMessage[] => {
  const known = new Set(list.map((m) => m.id));
  return [...list, ...page.filter((m) => !known.has(m.id))].sort(compareChatMessages);
};

/** Our optimistic messages (negative id) apart from the server ones. */
export const splitLocal = (list: IChatLocalMessage[]) => ({
  server: list.filter((m) => m.id > 0),
  local: list.filter((m) => m.id < 0),
});

/**
 * The window re-attached to the live end: the newest page plus whatever was
 * stashed while detached — minus anything the page already carries (a
 * stashed send that went through has its server copy in the page).
 */
export const attachWindow = (
  page: IChatMessage[],
  stash: IChatLocalMessage[],
): IChatLocalMessage[] =>
  stash.reduce<IChatLocalMessage[]>(
    (list, m) => (list.some((x) => sameMessage(x, m)) ? list : upsertMessage(list, m)),
    [...page].sort(compareChatMessages),
  );

/** Replace `m` if the window shows it; otherwise leave the window untouched. */
export const replaceIfPresent = (
  list: IChatLocalMessage[],
  m: IChatLocalMessage,
): IChatLocalMessage[] =>
  list.some((x) => sameMessage(x, m)) ? upsertMessage(list, m) : list;

/**
 * A live insert/update (socket event, a send's result) while detached: only
 * messages already in the window, or stashed, change. Anything else is newer
 * than the window and would land in the gap.
 */
export const applyWhileDetached = (
  window: { list: IChatLocalMessage[]; stash: IChatLocalMessage[] },
  m: IChatLocalMessage,
) => ({
  list: replaceIfPresent(window.list, m),
  stash: window.stash.map((x) => (sameMessage(x, m) ? m : x)),
});
